// Notificaciones push remotas (Firebase Cloud Messaging en Android).
//
// IMPORTANTE: sin `android/app/google-services.json`, PushNotifications.register()
// lanza una excepción NATIVA (`IllegalStateException: Default FirebaseApp is not
// initialized`) que el try/catch de JavaScript NO alcanza — crashea la app al
// arrancar (el clone fresco no trae el archivo porque está en .gitignore).
//
// Por eso el push remoto vive detrás de un flag de build: REACT_APP_PUSH_ENABLED.
// Solo se activa cuando Firebase está realmente configurado (google-services.json
// presente + flag en 'true'). Por defecto queda apagado → la app nunca crashea.
// Las notificaciones LOCALES (recordatorios de turno) no usan Firebase y van por
// otro plugin (@capacitor/local-notifications).
import { PushNotifications } from '@capacitor/push-notifications';
import { isNative, platform } from './platform';
import { supabase } from './supabase';
import { logStartupError } from './startup';

const PUSH_HABILITADO = process.env.REACT_APP_PUSH_ENABLED === 'true';

// Guarda/actualiza el token FCM del dispositivo en Supabase, asociado al usuario
// logueado. Fire-and-forget: si no hay sesión o falla, no interrumpe nada.
async function saveToken(token) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id || !token) return;
    // RPC SECURITY DEFINER: reasigna el token al usuario actual aunque el
    // dispositivo lo haya registrado antes con otra cuenta (ver migración).
    await supabase.rpc('register_device_token', { p_token: token, p_platform: platform });
  } catch {
    // función inexistente / sin red: ignorar, se reintenta en el próximo arranque
  }
}

// Inicializa push: pide permiso, registra en FCM y engancha listeners.
// Devuelve una función de cleanup. No-op en web y si el push no está habilitado
// (sin Firebase configurado), para no crashear la app.
export function initPush() {
  if (!isNative || !PUSH_HABILITADO) return () => {};

  const handles = [];
  const add = (p) => { handles.push(p); };

  (async () => {
    try {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt') perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') return;

      add(PushNotifications.addListener('registration', (token) => { saveToken(token.value); }));
      add(PushNotifications.addListener('registrationError', () => { /* sin Firebase llega acá */ }));
      add(PushNotifications.addListener('pushNotificationReceived', () => {
        // App en foreground: Android no muestra heads-up por defecto. Aquí se
        // podría disparar una notificación local para hacerla visible.
      }));
      add(PushNotifications.addListener('pushNotificationActionPerformed', () => {
        // El usuario tocó la notificación: aquí se podría navegar a una sección.
      }));

      await PushNotifications.register();
    } catch (err) {
      // google-services.json ausente o FCM no disponible: push deshabilitado.
      // Con el flag en 'true' Firebase DEBERÍA estar configurado, así que un
      // fallo aquí sí interesa loguearlo (sin romper el arranque).
      logStartupError('push.register', err);
    }
  })();

  return () => { handles.forEach((p) => p.then((h) => h.remove()).catch(() => {})); };
}
